import { Modal, Textarea, Input, Divider } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import DNButton from '../../Buttons/Button/DNButton'

export interface EmailSenderModalProps {
  from: string
  to?: string
  title: string
  subject: string
  body: string
  isOpen: boolean
  loading: boolean
  onClose
  onSend
}

const schema = z.object({
  email: z.string().email({ message: 'Invalid email' }),
  subject: z.string().max(100),
  body: z.string().max(2000),
})

const EmailSenderModal = ({
  title,
  subject,
  body,
  isOpen,
  onClose,
  loading,
  onSend,
  to,
}: EmailSenderModalProps) => {
  const form = useForm({
    initialValues: {
      email: to || '',
      subject: subject || '',
      body: body || '',
    },
    validate: zodResolver(schema),
  })

  const onFormSubmit = async (values) => {
    onSend({
      email: values.email,
    })
  }

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={title}
      closeOnEscape
      closeOnClickOutside
      // Explicit zIndex required: this modal is conditionally mounted
      // ({condition && <EmailSenderModal>}), so it can't use stackId
      // (unmounting skips stack deregistration). Must be above stack-managed
      // overlays (~201+) to render on top of drawers like VoEDetails.
      zIndex={210}
      transitionProps={{
        transition: 'slide-up',
        duration: 300,
        timingFunction: 'ease',
      }}
      overlayProps={{
        backgroundOpacity: 0.05,
      }}
      classNames={{
        title: 'font-semibold',
        body: 'flex flex-col gap-2 overflow-y-auto h-full',
        content:
          'fixed bottom-1.5 right-1.5 m-0 transition-all duration-300 rounded-2xl shadow-lg max-h-[100vh] w-[45vw]',
      }}
    >
      <form
        className="flex flex-1 flex-col gap-4"
        onSubmit={form.onSubmit((values) => onFormSubmit(values))}
      >
        <div className="flex h-full w-full flex-col gap-2">
          <div className="flex w-full flex-row items-baseline gap-2">
            <Input.Label>To:</Input.Label>
            <Textarea
              variant="unstyled"
              className="flex-1"
              placeholder="Enter recipient email..."
              required
              autosize
              maxRows={2}
              {...form.getInputProps('email')}
              classNames={{
                label: 'text-sm font-medium text-doubleNickel-gray-700',
              }}
            />
          </div>
          <Divider />

          <div className="flex w-full flex-row items-baseline gap-2">
            <Input.Label>Subject:</Input.Label>
            <Textarea
              variant="unstyled"
              className="flex-1"
              placeholder="Enter subject..."
              required
              autosize
              maxRows={2}
              readOnly
              {...form.getInputProps('subject')}
              classNames={{
                label: 'text-sm font-medium text-doubleNickel-gray-700',
              }}
            />
          </div>
          <Divider />

          <div className="flex max-h-[45vh] flex-1 flex-col gap-2 overflow-auto">
            <Textarea
              variant="unstyled"
              className="flex-1"
              placeholder="Enter email body..."
              readOnly
              autosize
              {...form.getInputProps('body')}
              classNames={{
                label: 'text-sm font-medium text-doubleNickel-gray-700',
                input:
                  'resize-none p-2 cursor-pointer caret-transparent overflow-auto [&[data-disabled]]:bg-doubleNickel-white [&[data-disabled]]:opacity-100 [&[data-disabled]]:text-doubleNickel-gray-600',
              }}
            />
          </div>
        </div>

        <Divider />

        <div className="flex w-full flex-row justify-end gap-3">
          <DNButton variant="outline" onClick={onClose}>
            Cancel
          </DNButton>
          <DNButton
            type="submit"
            variant="primary"
            loading={loading}
            className="min-w-20"
          >
            Send
          </DNButton>
        </div>
      </form>
    </Modal>
  )
}

export default EmailSenderModal
